import React, { useState, useEffect } from "react";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import DeleteIcon from "@mui/icons-material/Delete";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoIcon from "@mui/icons-material/Info";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ClearIcon from "@mui/icons-material/Clear";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PaidIcon from "@mui/icons-material/Paid";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import StarIcon from "@mui/icons-material/Star";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import QuickSelectionTabs from "./QuickSelectionTabs";
import PremiumDeliveryDetailsCard from "./PremiumDeliveryDetailsCard";
import StoreHeader from "./StoreHeader";
import CartStoreItems from "./CartStoreItems";
import DeliverySelectionCard from "./DeliverySelectionCard";
import { Coupon, CouponDiscountDetails } from "../../context/CartContext";

interface StoresWithDeliveryProps {
  storesWithDelivery: any[];
  bill: any;
  isRTL: boolean;
  t: (key: string) => string;
  currency: (num: number | string) => string;
  updateQuantity: (productId: number, branchId: number, newQty: number) => void;
  removeItem: (productId: number, branchId: number) => void;
  isLoadingDeliveries: boolean;
  selectedCities: Record<string, any>;
  setSelectedCities: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  storeDeliveryData: Record<string, any[]>;
  textAlignClass: string;
  currentCoupon?: Coupon | null;
  couponDiscount?: CouponDiscountDetails | null;
}

const StoresWithDelivery: React.FC<StoresWithDeliveryProps> = ({
  storesWithDelivery,
  bill,
  isRTL,
  t,
  currency,
  updateQuantity,
  removeItem,
  isLoadingDeliveries,
  selectedCities,
  setSelectedCities,
  storeDeliveryData,
  textAlignClass,
  currentCoupon,
  couponDiscount,
}) => {
  // State for expanded/collapsed delivery sections
// خلي الديفولت كده:
const [expandedStores, setExpandedStores] = useState<Record<number, boolean>>(
  () => {
    const initialExpanded: Record<number, boolean> = {};
    storesWithDelivery.forEach((s) => {
      initialExpanded[s.storeId] = true; // كل ستور مفتوح افتراضياً
    });
    return initialExpanded;
  }
);
  // State for loading animations
  const [loadingAnimation, setLoadingAnimation] = useState(true);
  // State for showing provider info
  const [showProviderInfo, setShowProviderInfo] = useState<
    Record<number, boolean>
  >({});

  useEffect(() => {
    // Stop loading animation after 500ms
    const timer = setTimeout(() => {
      setLoadingAnimation(false);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  const toggleStoreExpansion = (storeId: number) => {
    setExpandedStores((prev) => ({
      ...prev,
      [storeId]: !prev[storeId],
    }));
  };

  const toggleProviderInfo = (storeId: number) => {
    setShowProviderInfo((prev) => ({
      ...prev,
      [storeId]: !prev[storeId],
    }));
  };

  const calculateDeliveryDate = (days: number) => {
    const today = new Date();
    const deliveryDate = new Date(today);
    deliveryDate.setDate(today.getDate() + days);

    const options: Intl.DateTimeFormatOptions = {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    };

    return deliveryDate.toLocaleDateString(isRTL ? "ar-EG" : "en-US", options);
  };

  const getDeliveryCardColor = (storeId: number) => {
    const isSelected = selectedCities[storeId];
    return isSelected
      ? "from-emerald-50 to-green-50 border-emerald-200"
      : "from-gray-50 to-gray-100 border-gray-200";
  };

  const getRandomProvider = () => {
    const providers = [
      { name: "Speed Express", rating: 4.8, deliveries: 1250 },
      { name: "QuickShip", rating: 4.9, deliveries: 980 },
      { name: "City Logistics", rating: 4.7, deliveries: 2100 },
      { name: "Fast Track", rating: 4.6, deliveries: 1750 },
      { name: "Premium Delivery", rating: 4.9, deliveries: 3200 },
    ];
    return providers[Math.floor(Math.random() * providers.length)];
  };

  // Loading animation component

  const ProviderInfoCard = ({
    storeId,
    cityName,
  }: {
    storeId: number;
    cityName: string;
  }) => {
    const provider = getRandomProvider();

    return (
      <div className="mt-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200 animate-fadeInUp">
        <div
          className={`flex items-start justify-between ${
            isRTL ? "flex-row-reverse" : ""
          }`}
        >
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-3">
              <VerifiedUserIcon className="text-blue-600" />
              <h4 className="font-semibold text-blue-800">
                {t("cart.providerInfo")}
              </h4>
            </div>

            <p className="text-sm text-blue-700 mb-3">
              {t("cart.providerMessage").replace("{city}", cityName)}
            </p>

            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <PersonOutlineIcon className="text-blue-500 text-sm" />
                <span className="text-sm text-blue-800 font-medium">
                  {provider.name}
                </span>
              </div>

              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1">
                  <StarIcon className="text-yellow-500 text-sm" />
                  <span className="text-sm text-blue-700">
                    {provider.rating}/5
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <LocalShippingIcon className="text-blue-500 text-sm" />
                  <span className="text-sm text-blue-700">
                    {provider.deliveries.toLocaleString()}+{" "}
                    {t("cart.deliveries")}
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-3 pt-3 border-t border-blue-200">
              <div className="flex items-center gap-2 text-xs text-blue-600">
                <ConfirmationNumberIcon fontSize="small" />
                <span>{t("cart.trackingAvailable")}</span>
              </div>
            </div>
          </div>

          <button
            onClick={() => toggleProviderInfo(storeId)}
            className="text-blue-500 hover:text-blue-700"
          >
            <ClearIcon />
          </button>
        </div>
      </div>
    );
  };

  return (
    <>
      {storesWithDelivery.length > 0 && (
        <div className="mb-10 animate-fadeIn">
          {/* Header with enhanced animation */}
          {currentCoupon && (
            <div className="mb-4 p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
              <div
                className={`flex items-center gap-2 ${
                  isRTL ? "flex-row-reverse" : ""
                }`}
              >
                <span className="text-green-700 font-medium">
                  {t("cart.couponApplied")}: {currentCoupon.code}(
                  {couponDiscount?.savingsPercentage}% {t("cart.savings")})
                </span>
              </div>
            </div>
          )}
          <div
            className={`flex items-center gap-3 mb-6 ${
              isRTL ? "flex-row-reverse" : ""
            } animate-slideDown`}
          >
            <div className="relative">
              <div className="absolute inset-0 bg-green-500 rounded-full animate-ping opacity-20"></div>
              <LocalShippingIcon className="text-white bg-gradient-to-r from-green-500 to-emerald-500 p-2 rounded-lg text-2xl relative animate-bounce-slow" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-800">
                {t("cart.storesWithDelivery")}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {t("cart.selectDeliveryCities")}
              </p>
            </div>
            <div className="ml-auto bg-gradient-to-r from-green-500 to-emerald-500 text-white px-3 py-1 rounded-full text-sm font-medium animate-pulse-slow">
              {storesWithDelivery.length} {t("cart.stores")}
            </div>
          </div>

          {bill?.stores?.map((s: any) => (
            <div
              key={s.storeId}
              className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-xl p-6 mb-8 border border-gray-200 hover:border-green-300 transition-all duration-500 hover:shadow-2xl animate-slideUp"
              style={{ animationDelay: `${s.storeId * 100}ms` }}
            >
              {/* Store Header */}

              <StoreHeader
                storeId={s.storeId}
                store={s.store}
                subtotal={s.subtotal}
                itemCount={s.items.length}
                isSelected={!!selectedCities[s.storeId]}
                isExpanded={expandedStores[s.storeId]}
                isRTL={isRTL}
                t={t}
                currency={currency}
                onToggleExpand={() => toggleStoreExpansion(s.storeId)}
                onStoreClick={() => {
                  // Navigate to store page
                  console.log("Navigate to store:", s.storeId);
                }}
                variant="premium" // أو "default" أو "compact"
              />
              <CartStoreItems
                storeId={s.storeId}
                items={s.items}
                expandedStores={expandedStores}
                isRTL={isRTL}
                currency={currency}
                t={t}
                updateQuantity={updateQuantity}
                removeItem={removeItem}
              />

              {/* Enhanced Delivery Select Section with Premium Design */}
              <DeliverySelectionCard
                storeId={s.storeId}
                isLoadingDeliveries={isLoadingDeliveries}
                isRTL={isRTL}
                selectedCity={selectedCities[s.storeId]}
                storeDeliveryOptions={storeDeliveryData[s.storeId] || []}
                getDeliveryCardColor={getDeliveryCardColor}
                t={t}
                showProviderInfo={showProviderInfo[s.storeId]}
                getRandomProvider={getRandomProvider}
                calculateDeliveryDate={calculateDeliveryDate}
                onToggleProviderInfo={() => toggleProviderInfo(s.storeId)}
                onSelectCity={(city) =>
                  setSelectedCities((prev) => ({ ...prev, [s.storeId]: city }))
                }
                onClearCity={() => {
                  setSelectedCities((prev) => ({ ...prev, [s.storeId]: null }));
                  setShowProviderInfo((prev) => ({
                    ...prev,
                    [s.storeId]: false,
                  }));
                }}
              />
            </div>
          ))}
        </div>
      )}
    </>
  );
};

// Add missing icon
const CalendarTodayIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z" />
  </svg>
);

export default StoresWithDelivery;
